import React, { useState, useEffect } from 'react';
import { useAdmin } from './AdminContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, Edit, Ban, RotateCcw, Shield } from 'lucide-react';

const ROLES = ['admin', 'operations', 'accounting'];
const ROLE_COLORS = { admin: '#B8962E', operations: '#3B82F6', accounting: '#10B981' };

export default function AdminUsersPage() {
  const { authFetch, user: me } = useAdmin();
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'operations' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await authFetch('/api/admin/users'); if (r.ok) setUsers(await r.json()); } catch {}
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', email: '', password: '', role: 'operations' }); setModalOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: '', role: u.role }); setModalOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email required'); return; }
    if (!editing && !form.password) { toast.error('Password required for new user'); return; }
    setSaving(true);
    try {
      const body = { name: form.name, email: form.email, role: form.role };
      if (form.password) body.password = form.password;
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/admin/users/${editing.id}` : '/api/admin/users';
      const res = await authFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) { toast.success(editing ? 'User updated' : 'User created'); setModalOpen(false); load(); }
      else { const e = await res.json(); toast.error(e.detail || 'Error'); }
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    if (u.id === me?.id) { toast.error('Cannot deactivate yourself'); return; }
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await authFetch(`/api/admin/users/${u.id}`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
      if (res.ok) { toast.success(`User ${newStatus}`); load(); }
    } catch {}
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>User Management</h1>
        <Button onClick={openNew} className="gap-2 font-semibold" style={{ background: '#B8962E', color: '#fff' }} data-testid="add-user-btn"><Plus className="w-4 h-4" /> Add User</Button>
      </div>

      <div className="space-y-3">
        {users.map(u => (
          <Card key={u.id} className="border border-gray-200" data-testid={`user-${u.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: ROLE_COLORS[u.role] }}>
                    {u.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold" style={{ color: '#1B2A6B' }}>{u.name}</span>
                      <Badge className="text-xs capitalize" style={{ background: `${ROLE_COLORS[u.role]}22`, color: ROLE_COLORS[u.role] }}>{u.role}</Badge>
                      <Badge variant={u.status === 'active' ? 'default' : 'secondary'} className="text-xs" style={u.status === 'active' ? { background: '#10B98122', color: '#10B981' } : {}}>{u.status}</Badge>
                      {u.id === me?.id && <Badge className="text-xs bg-blue-100 text-blue-600">You</Badge>}
                    </div>
                    <p className="text-sm text-gray-500">{u.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(u)} className="gap-1" data-testid={`edit-user-${u.id}`}><Edit className="w-3.5 h-3.5" /> Edit</Button>
                  {u.id !== me?.id && (
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(u)} className={`gap-1 ${u.status === 'active' ? 'text-red-600 border-red-200' : 'text-green-600 border-green-200'}`}>
                      {u.status === 'active' ? <><Ban className="w-3.5 h-3.5" /> Disable</> : <><RotateCcw className="w-3.5 h-3.5" /> Enable</>}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid="user-modal">
          <DialogHeader><DialogTitle style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>{editing ? 'Edit User' : 'New User'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="user-name" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Email *</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} type="email" data-testid="user-email" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">{editing ? 'New Password (leave blank to keep)' : 'Password *'}</Label><Input value={form.password} onChange={e => setForm({...form, password: e.target.value})} type="password" data-testid="user-password" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Role *</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} style={{ background: '#B8962E', color: '#fff' }} data-testid="user-save">{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
